import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Check, Tag, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalCard } from '@/components/ui/BrutalCard';
import { BrutalInput } from '@/components/ui/BrutalInput';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { 
  formatDiscountMessage, 
  getFormattedProductList,
  validateCouponGlobally,
  hasValidCoupons,
  saveCouponData,
  type CouponResult 
} from '@/lib/couponUtils';
import i18n from '@/i18n/config';

interface PurchaseCreditsModalProps {
  open: boolean;
  onClose: () => void;
}

interface CreditPackage {
  id: string;
  credits: number;
  price: number;
  priceId: string;
}

const creditPackages: CreditPackage[] = [
  { id: 'job_posting_single', credits: 1, price: 30, priceId: 'price_1SixD2HMNyARPyM5p54sUvJw' },
  { id: 'job_posting_pack_3', credits: 3, price: 75, priceId: 'price_1SixDCHMNyARPyM5caMtFmp5' },
  { id: 'job_posting_pack_5', credits: 5, price: 100, priceId: 'price_1SixDQHMNyARPyM5MbxPeujB' },
];

export function PurchaseCreditsModal({ open, onClose }: PurchaseCreditsModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupons, setAppliedCoupons] = useState<Record<string, CouponResult | null>>({});
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponChecked, setCouponChecked] = useState(false);

  // Reusable coupon validation function - validates against ALL products globally
  const validateCouponForJobCredits = useCallback(async (code: string, showToast = false) => {
    if (!code.trim()) return;
    
    setCouponLoading(true);
    setCouponError(null);
    
    try {
      // Validate against ALL product types globally
      const allResults = await validateCouponGlobally(supabase, code.trim().toUpperCase());
      
      // Check if any product is valid globally
      const hasAnyValid = hasValidCoupons(allResults);
      
      if (hasAnyValid) {
        // Save ALL results to sessionStorage
        saveCouponData(code.trim().toUpperCase(), allResults);
        setAppliedCoupons(allResults);
        
        if (showToast) {
          const locale = i18n.language || 'it';
          const formattedProducts = getFormattedProductList(allResults, locale);
          const sample = Object.values(allResults).find(r => r?.valid);
          
          toast({
            title: t('common.success'),
            description: t('pricing.coupon.validDynamic', { 
              products: formattedProducts,
              discount: formatDiscountMessage(sample?.discountType || 'percentage', sample?.discountValue || 0)
            }),
          });
        }
      } else {
        setCouponError(t('pricing.coupon.invalid'));
      }
    } catch (error: any) {
      console.error('Coupon validation error:', error);
      setCouponError(error.message || t('jobs.credits.couponError'));
    } finally {
      setCouponLoading(false);
    }
  }, [t, toast]);

  // Read coupon from sessionStorage when modal opens
  useEffect(() => {
    if (!open || couponChecked) return;
    
    const savedCoupon = sessionStorage.getItem('pendingCoupon');
    
    if (savedCoupon && Object.keys(appliedCoupons).length === 0) {
      setCouponCode(savedCoupon);
      validateCouponForJobCredits(savedCoupon, false);
    }
    
    setCouponChecked(true);
  }, [open, couponChecked, appliedCoupons, validateCouponForJobCredits]);

  // Reset couponChecked when modal closes
  useEffect(() => {
    if (!open) {
      setCouponChecked(false);
      setCouponError(null);
    }
  }, [open]);

  const handleApplyCoupon = async () => {
    await validateCouponForJobCredits(couponCode.trim(), true);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupons({});
    setCouponCode('');
    setCouponError(null);
    
    // Remove from sessionStorage
    sessionStorage.removeItem('pendingCoupon');
    sessionStorage.removeItem('appliedCouponData');
  };

  const handlePurchase = async (packageId: string, priceId: string) => {
    setLoading(packageId);

    try {
      const appliedCoupon = appliedCoupons[packageId];
      
      const { data, error } = await supabase.functions.invoke('purchase-job-credits', {
        body: { 
          package_id: packageId, 
          price_id: priceId,
          coupon_code: Object.keys(appliedCoupons).length > 0 ? couponCode : undefined,
        },
      });

      if (error) throw error;

      // If 100% discount, credits were added directly
      if (data?.success) {
        toast({
          title: t('common.success'),
          description: data.message,
        });
        queryClient.invalidateQueries({ queryKey: ['job-credits'] });
        onClose();
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Purchase error:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(null);
    }
  };

  const getDisplayPrice = (pkg: CreditPackage) => {
    const appliedCoupon = appliedCoupons[pkg.id];
    if (appliedCoupon) {
      return {
        original: pkg.price,
        discounted: appliedCoupon.finalPrice,
        discountPercent: appliedCoupon.discountPercent,
        isFree: appliedCoupon.discountPercent === 100,
      };
    }
    return {
      original: pkg.price,
      discounted: pkg.price,
      discountPercent: 0,
      isFree: false,
    };
  };

  const hasCouponApplied = Object.keys(appliedCoupons).length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] border-[3px] border-border shadow-[6px_6px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {t('jobs.credits.purchaseTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('jobs.credits.purchaseDesc')}
          </DialogDescription>
        </DialogHeader>

        {/* Coupon Input */}
        <div className="flex gap-2 mt-2">
          <div className="flex-1 relative">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <BrutalInput
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder={t('jobs.credits.couponPlaceholder')}
              className="pl-10"
              disabled={hasCouponApplied}
            />
          </div>
          {hasCouponApplied ? (
            <BrutalButton
              variant="outline"
              onClick={handleRemoveCoupon}
              className="shrink-0"
            >
              <X className="w-4 h-4" />
            </BrutalButton>
          ) : (
            <BrutalButton
              onClick={handleApplyCoupon}
              disabled={couponLoading || !couponCode.trim()}
              className="shrink-0"
            >
              {couponLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t('jobs.credits.applyCoupon')
              )}
            </BrutalButton>
          )}
        </div>
        
        {couponError && (
          <p className="text-sm text-destructive mt-1">{couponError}</p>
        )}
        
        {hasCouponApplied && (() => {
          const sample = Object.values(appliedCoupons).find(Boolean);
          if (!sample) return null;
          const discount = formatDiscountMessage(sample.discountType, sample.discountValue);
          const locale = i18n.language || 'it';
          const formattedProducts = getFormattedProductList(appliedCoupons, locale);
          return (
            <div className="p-3 bg-primary/10 border-[3px] border-primary">
              <p className="font-heading font-bold text-dark text-sm">
                {t('pricing.coupon.couponConfirmation', { discount })}
              </p>
              <p className="font-body text-xs text-muted-foreground mt-1">
                {t('pricing.coupon.appliesToFormatted', { products: formattedProducts })}
              </p>
            </div>
          );
        })()}

        <div className="grid gap-4 mt-4">
          {creditPackages.map((pkg) => {
            const priceInfo = getDisplayPrice(pkg);
            
            return (
              <BrutalCard key={pkg.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-heading font-bold text-lg">
                      {pkg.credits} {pkg.credits === 1 ? t('jobs.credits.credit') : t('jobs.credits.credits')}
                    </h4>
                    {priceInfo.discountPercent > 0 && priceInfo.discountPercent < 100 && (
                      <span className="text-sm text-success font-medium">
                        {t('jobs.credits.savePercent', { percent: priceInfo.discountPercent })}
                      </span>
                    )}
                    {priceInfo.isFree && (
                      <span className="text-sm text-success font-bold">
                        {t('jobs.credits.freeWithCoupon')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      {priceInfo.discountPercent > 0 ? (
                        <div className="flex flex-col items-end">
                          <span className="text-muted-foreground line-through text-sm">
                            €{priceInfo.original}
                          </span>
                          <span className="font-heading text-2xl font-bold text-success">
                            {priceInfo.isFree ? t('common.free') : `€${priceInfo.discounted}`}
                          </span>
                        </div>
                      ) : (
                        <span className="font-heading text-2xl font-bold">€{pkg.price}</span>
                      )}
                    </div>
                    <BrutalButton
                      onClick={() => handlePurchase(pkg.id, pkg.priceId)}
                      disabled={loading !== null}
                    >
                      {loading === pkg.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : priceInfo.isFree ? (
                        t('jobs.credits.activate')
                      ) : (
                        t('jobs.credits.buy')
                      )}
                    </BrutalButton>
                  </div>
                </div>
              </BrutalCard>
            );
          })}
        </div>

        <div className="mt-4 p-4 bg-muted rounded-lg">
          <h5 className="font-medium mb-2">{t('jobs.credits.whatYouGet')}</h5>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-success" />
              {t('jobs.credits.benefit1')}
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-success" />
              {t('jobs.credits.benefit2')}
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-success" />
              {t('jobs.credits.benefit3')}
            </li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
